import { useState, useEffect, useCallback, useRef } from "react";
import { dismissModal } from "@/utils/modalClose";
import { createPortal } from "react-dom";
import {
  IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonSpinner,
  IonCard, IonList, IonItem, IonLabel, IonInput, IonSegment, IonSegmentButton,
  IonRadioGroup, IonRadio, IonSelect, IonSelectOption,
} from "@ionic/react";
import { closeOutline, trashOutline, downloadOutline } from "ionicons/icons";
import SignaturePad, { SignatureBox, SignatureImage, useDarkMode } from "./SignaturePad";
import UploadDropzone, { UploadedImage } from "./UploadDropzone";
import { isUserLoggedIn, userHeaders } from "@/utils/userAuth";
import { toast } from "@/utils/toast";
import { confirmAlert } from "@/utils/confirmAlert";
import { saveBlob } from "@/utils/archivedFile";
import { isMobileBrowser, openDownloadInNewTab } from "@/utils/pendingDownload";
import { readImageFile } from "@/utils/imageFile";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Saved signatures and logos (the /app Branding page): a Pro or Enterprise
// subscriber keeps reusable images, and every generator field of
// that kind offers them through a "Use saved" picker. One fetch serves every
// button on a form via a short-lived module cache; managers invalidate it.
export const ASSET_KIND_LABELS = { signature: "Signatures", logo: "Logos" };
const ASSET_EVENT = "mintslip-assets-updated";

// The account this cache belongs to: signing out and back in as someone else
// inside the 30s window must not serve the previous person's assets or colour.
export function currentUserId() {
  try { return (JSON.parse(localStorage.getItem("userInfo") || "{}") || {}).id || ""; }
  catch { return ""; }
}

// The Branding page is Pro/Enterprise only and its sidebar link is hidden for
// everyone else. Remembering the last answer per account lets the sidebar
// render correctly on the first paint instead of flashing the link in or out
// while /api/user/assets is still in flight.
const accessKey = (uid) => `appBrandingAccess:${uid || "anon"}`;
export function rememberAssetAccess(uid, ok) {
  try {
    if (ok) localStorage.setItem(accessKey(uid), "1");
    else localStorage.removeItem(accessKey(uid));
  } catch { /* storage off */ }
}
export function rememberedAssetAccess(uid) {
  try { return localStorage.getItem(accessKey(uid)) === "1"; } catch { return false; }
}

// The business being operated as, remembered per account so notification
// creators can stamp it synchronously wherever they run (payment-success
// included). {id, primary} while a plan carries businesses; null otherwise.
const activeBizKey = (uid) => `appActiveBiz:${uid || "anon"}`;
export function rememberActiveBusiness(uid, info) {
  try {
    if (info && info.id) localStorage.setItem(activeBizKey(uid), JSON.stringify({ id: info.id, primary: !!info.primary }));
    else localStorage.removeItem(activeBizKey(uid));
  } catch { /* storage off */ }
}
export function rememberedActiveBusiness(uid) {
  try { return JSON.parse(localStorage.getItem(activeBizKey(uid)) || "null"); } catch { return null; }
}

const EMPTY = { hasAccess: false, assets: [], primaryColor: null, business: null, businesses: [], activeBusinessId: null };
let cache = null; // { at, uid, data: { hasAccess, assets, primaryColor } }
export async function fetchSavedAssets(force = false) {
  if (!isUserLoggedIn()) return EMPTY;
  const uid = currentUserId();
  if (!force && cache && cache.uid === uid && Date.now() - cache.at < 30000) return cache.data;
  try {
    const res = await fetch(`${BACKEND_URL}/api/user/assets`, { headers: userHeaders() });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.success) throw new Error();
    // The colour only ever comes from this response: it is plan-gated, so a
    // lapsed subscriber gets no key and the app clears what it had cached.
    const data = { hasAccess: !!d.hasAccess, assets: d.assets || [], primaryColor: d.primaryColor || null, business: d.business || null, businesses: d.businesses || [], activeBusinessId: d.activeBusinessId || null };
    cache = { at: Date.now(), uid, data };
    const activeRow = data.hasAccess ? data.businesses.find((b) => b.id === data.activeBusinessId) : null;
    rememberActiveBusiness(uid, activeRow || null);
    return data;
  } catch {
    return cache && cache.uid === uid ? cache.data : EMPTY;
  }
}
export function invalidateSavedAssets() {
  cache = null;
  try { window.dispatchEvent(new Event(ASSET_EVENT)); } catch { /* ignore */ }
}
// Fetch fresh first, then tell every hook: they read the new cache rather
// than each fetching again (a business switch waits on this).
export async function refreshSavedAssets() {
  cache = null;
  const d = await fetchSavedAssets(true);
  try { window.dispatchEvent(new Event(ASSET_EVENT)); } catch { /* ignore */ }
  return d;
}
// The last-known assets for the current user, read synchronously. Every /app
// page mounts its own layout, so seeding initial state from this keeps the
// sidebar's business row/title steady across navigation instead of blinking
// from empty while the fetch reruns. The effect still refreshes in place.
export function peekSavedAssets() {
  if (!isUserLoggedIn()) return EMPTY;
  return cache && cache.uid === currentUserId() ? cache.data : EMPTY;
}

// The account's saved assets, kind-filtered when asked; refreshes when a
// manager saves or deletes anywhere in the app.
export function useSavedAssets(kind = null) {
  const [state, setState] = useState(() => {
    const seed = peekSavedAssets();
    return { ...seed, loading: isUserLoggedIn() && seed === EMPTY };
  });
  const load = useCallback(async (force = false) => {
    const d = await fetchSavedAssets(force);
    setState({ ...d, loading: false });
  }, []);
  useEffect(() => {
    load();
    // an invalidation emptied the cache, so this fetches; a refresh filled
    // it, so this reads it
    const on = () => load();
    window.addEventListener(ASSET_EVENT, on);
    return () => window.removeEventListener(ASSET_EVENT, on);
  }, [load]);
  return {
    hasAccess: state.hasAccess,
    assets: kind ? state.assets.filter((a) => a.kind === kind) : state.assets,
    primaryColor: state.primaryColor,
    business: state.business,
    businesses: state.businesses,
    activeBusinessId: state.activeBusinessId,
    loading: state.loading,
    refresh: () => load(true),
  };
}

// A small preview of one asset: signatures in the theme-aware ink box,
// logos contained on a light chip (many are white-on-transparent)
export function AssetThumb({ asset, height = 44, dark }) {
  if (asset.kind === "signature") {
    return (
      <span className="asset-thumb" style={{ height }}>
        <SignatureImage src={asset.data} dark={dark} />
      </span>
    );
  }
  return (
    <span className="asset-thumb asset-thumb-logo" style={{ height }}>
      <img src={asset.data} alt="" />
    </span>
  );
}

const sheetShell = (isMobile, maxWidth) => ({
  backdrop: { position: "fixed", inset: 0, zIndex: 10014, background: isMobile ? "var(--ion-background-color)" : "rgba(0,0,0,0.55)", display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: "center" },
  sheet: { background: "var(--ion-background-color)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: isMobile ? "100%" : maxWidth, height: isMobile ? "100%" : "auto", maxHeight: isMobile ? "100%" : "85vh", overflow: "hidden" },
});

const XButton = ({ onClose }) => (
  <IonButtons slot="start">
    <IonButton fill="clear" shape="round" onClick={onClose} aria-label="Close">
      <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
        <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
      </span>
    </IonButton>
  </IonButtons>
);

// A generator field's saved-asset dropdown (an outline Ionic select): the
// saved signatures — or logos — by name, the default preselected and applied
// to the field on first render while it is still empty, so the selection is
// what the document gets. Picking another applies it; None blanks the field
// (a second party's signature stays empty, a logo comes off). Renders nothing
// for guests, plans without the feature, or an empty library.
//
// The picked id is remembered rather than derived from the image alone: a
// page may transform what it stores (the paystubs resize a logo to fit their
// header), so the applied bytes need not equal the saved asset's.
export function SavedAssetSelect({ kind, current, onApply, style, label }) {
  const { hasAccess, assets } = useSavedAssets(kind);
  const applied = useRef(false);
  const [picked, setPicked] = useState("");
  const def = assets.find((a) => a.isDefault) || null;
  useEffect(() => {
    if (applied.current || current || !hasAccess || !def) return;
    applied.current = true;
    setPicked(def.id);
    onApply(def);
  }, [hasAccess, assets]); // eslint-disable-line
  if (!hasAccess || assets.length === 0) return null;
  const value = !current
    ? ""
    : (assets.some((a) => a.id === picked) ? picked : (assets.find((a) => a.data === current) || {}).id || "");
  return (
    <IonSelect
      fill="outline" labelPlacement="floating" label={label} interface="popover"
      value={value} style={style} className={`saved-asset-select saved-${kind}-select`}
      onIonChange={(e) => {
        const id = e.detail.value;
        if (!id) { setPicked(""); if (current) onApply(null); return; }
        const a = assets.find((x) => x.id === id);
        if (!a) return;
        setPicked(id);
        if (a.data !== current) onApply(a);
      }}
    >
      <IonSelectOption value="">None</IonSelectOption>
      {assets.map((a) => (
        <IonSelectOption key={a.id} value={a.id}>{a.name}{a.isDefault ? " · Default" : ""}</IonSelectOption>
      ))}
    </IonSelect>
  );
}

export const SavedSignatureSelect = ({ label = "Saved signature", ...rest }) => (
  <SavedAssetSelect kind="signature" label={label} {...rest} />
);
export const SavedLogoSelect = ({ label = "Saved logo", ...rest }) => (
  <SavedAssetSelect kind="logo" label={label} {...rest} />
);

// ── The manager the Branding page opens: the saved list + an add card ──────
export function AssetManagerModal({ kind, onClose: onCloseNow }) {
  const onClose = (e) => dismissModal(onCloseNow, e); // animated exit, then the real close

  const isMobile = window.innerWidth < 768;
  const dark = useDarkMode();
  const { assets } = useSavedAssets(kind);
  const label = kind === "signature" ? "signature" : "logo";
  const [name, setName] = useState("");
  const [draft, setDraft] = useState(null);        // the image about to be saved (data URL)
  const [sigMode, setSigMode] = useState("draw");  // signatures: draw | upload
  const [padKey, setPadKey] = useState(0);         // remounts the pad to clear it after a save
  const [busy, setBusy] = useState(false);

  // 1.4 MB of image is about 1.9 MB of data URL, inside the server's cap
  const readFile = async (file) => {
    try { setDraft(await readImageFile(file, { label: kind === "signature" ? "Signature" : "Logo", maxBytes: 1_400_000 })); }
    catch (err) { toast.error(err.message); }
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/assets`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ kind, name: name.trim(), data: draft }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not save");
      toast.success(`${kind === "signature" ? "Signature" : "Logo"} saved`);
      setDraft(null); setName(""); setPadKey((k) => k + 1);
      invalidateSavedAssets();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  // A signature is the user's own image: hand it back as a file, through the
  // same blob delivery the paid documents use (a data-URL anchor is ignored
  // by several browsers) — a new tab on phone browsers, in place elsewhere
  const download = (a) => {
    try {
      const m = a.data.match(/^data:(image\/([\w+.-]+));base64,(.*)$/s);
      if (!m) throw new Error();
      const bytes = atob(m[3]);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: m[1] });
      const ext = m[2] === "jpeg" ? "jpg" : m[2].replace(/[^\w]/g, "") || "png";
      const fileName = `${(a.name || "signature").replace(/[\/:*?"<>|]+/g, "").trim() || "signature"}.${ext}`;
      if (isMobileBrowser()) openDownloadInNewTab({ url: URL.createObjectURL(blob), fileName });
      else saveBlob(blob, fileName);
    } catch {
      toast.error("Could not download that signature");
    }
  };

  // Signatures pick a default like the Payment methods cards: the radio at
  // the row's end saves at once
  const defaultId = (assets.find((a) => a.isDefault) || {}).id || "";
  const makeDefault = async (id) => {
    const a = assets.find((x) => x.id === id);
    if (!a || a.isDefault) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/assets/${id}/default`, { method: "PUT", headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not save");
      toast.success(`Default signature set to ${a.name}`);
      invalidateSavedAssets();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (a) => {
    if (!(await confirmAlert({ header: `Remove ${a.name}?`, message: `The ${label} is removed from your saved ${label}s. Documents already made with it keep it.`, confirmText: "Remove" }))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/assets/${a.id}`, { method: "DELETE", headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not remove");
      toast.success(`${a.name} removed`);
      invalidateSavedAssets();
    } catch (err) { toast.error(err.message); }
  };

  const s = sheetShell(isMobile, 600);
  return createPortal(
    <div className="modal-backdrop pm-modal assets-modal" style={s.backdrop}>
      <div className="modal-slide-up" style={s.sheet}>
        <IonHeader>
          <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
            <XButton onClose={onClose} />
            <IonTitle style={{ fontWeight: 700 }}>{ASSET_KIND_LABELS[kind]}</IonTitle>
          </IonToolbar>
        </IonHeader>
        {/* one card, like the admin URLs sheet: a bold title over the saved
            list, then the shared sub-header bar leading into the add form */}
        <div className="pm-body">
          <IonCard className="pm-card sheet-card">
            <div className="sheet-card-title">Your {label}s</div>
            {assets.length === 0 ? (
              <p className="pm-empty">No {label}s saved yet. Add one below.</p>
            ) : (() => {
              const rows = (
                <IonList lines="full">
                  {assets.map((a, i) => (
                    <IonItem key={a.id} lines={i === assets.length - 1 ? "none" : undefined} className="pm-row">
                      <AssetThumb asset={a} dark={dark} />
                      <IonLabel>
                        {a.name}
                        <p>{[a.createdAt ? `Saved ${new Date(a.createdAt).toLocaleDateString()}` : "", a.isDefault ? "Default" : ""].filter(Boolean).join(" · ")}</p>
                      </IonLabel>
                      {kind === "signature" && (
                        <IonButton slot="end" fill="clear" color="medium" size="small" onClick={() => download(a)} aria-label={`Download ${a.name}`} title="Download">
                          <IonIcon slot="icon-only" icon={downloadOutline} />
                        </IonButton>
                      )}
                      <IonButton slot="end" fill="clear" color="danger" size="small" onClick={() => remove(a)} aria-label={`Remove ${a.name}`}>
                        <IonIcon slot="icon-only" icon={trashOutline} />
                      </IonButton>
                      {kind === "signature" && <IonRadio slot="end" value={a.id} aria-label={`Make ${a.name} the default`} />}
                    </IonItem>
                  ))}
                </IonList>
              );
              return kind === "signature"
                ? <IonRadioGroup value={defaultId} onIonChange={(e) => makeDefault(e.detail.value)}>{rows}</IonRadioGroup>
                : rows;
            })()}

            <div className="admin-subbar sheet-card-head">Add a {label}</div>
            <div className="pm-form">
              <IonInput fill="outline" labelPlacement="floating" label={`${kind === "signature" ? "Signature" : "Logo"} name`} value={name} maxlength={40} onIonInput={(e) => setName(e.detail.value ?? "")} />
              {kind === "signature" ? (
                <>
                  <IonSegment mode="ios" value={sigMode} onIonChange={(e) => { setSigMode(e.detail.value); setDraft(null); setPadKey((k) => k + 1); }}>
                    <IonSegmentButton value="draw"><IonLabel>Draw</IonLabel></IonSegmentButton>
                    <IonSegmentButton value="upload"><IonLabel>Upload</IonLabel></IonSegmentButton>
                  </IonSegment>
                  {sigMode === "draw" && <SignaturePad key={padKey} height={150} onChange={(dataUrl) => setDraft(dataUrl)} />}
                  {sigMode === "upload" && (
                    draft ? (
                      <SignatureBox height={150}><SignatureImage src={draft} dark={dark} /></SignatureBox>
                    ) : (
                      <UploadDropzone accept="image/*" onFile={readFile} />
                    )
                  )}
                </>
              ) : (
                draft ? (
                  <UploadedImage src={draft} onRemove={() => setDraft(null)} />
                ) : (
                  <UploadDropzone accept="image/*" onFile={readFile} />
                )
              )}
              <IonButton expand="block" color="success" disabled={!draft || busy} onClick={save}>
                {busy ? <IonSpinner name="crescent" /> : `Save ${label}`}
              </IonButton>
            </div>
          </IonCard>
        </div>
      </div>
    </div>,
    document.querySelector("ion-app") || document.body
  );
}
